import { cn } from '@/lib/utils'
import type { LegitimacyStatus } from '@/types'
import { AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react'

/**
 * Small badge near the company name signalling our confidence that the
 * posting is real and worth engaging with. Block G of the career-ops rubric.
 */

interface LegitimacyBadgeProps {
  legitimacy: LegitimacyStatus
  className?: string
}

const VARIANTS: Record<
  LegitimacyStatus,
  { label: string; icon: typeof CheckCircle2; classes: string }
> = {
  high_confidence: {
    label: 'Verified posting',
    icon: CheckCircle2,
    classes: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  },
  proceed_with_caution: {
    label: 'Proceed with caution',
    icon: AlertTriangle,
    classes: 'bg-yellow-50 text-yellow-700 ring-yellow-200',
  },
  suspicious: {
    label: 'Suspicious',
    icon: ShieldAlert,
    classes: 'bg-red-50 text-red-700 ring-red-200',
  },
}

export function LegitimacyBadge({ legitimacy, className }: LegitimacyBadgeProps) {
  const variant = VARIANTS[legitimacy]
  const Icon = variant.icon
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1',
        variant.classes,
        className,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {variant.label}
    </span>
  )
}
